/**
 * 增强的请求工具
 *
 * 功能特性：
 * 1. 集成防重复请求
 * 2. 集成API缓存
 * 3. 支持请求取消
 * 4. 统一错误处理
 * 5. 请求状态管理
 */

import { get, post, put, del, ApiResponse } from '@/utils/request';
import { createRequestDeduplicator } from '@/hooks/common/useRequestDeduplication';
import { apiCache } from '@/utils/cache/apiCache';
import { getCacheTTLForUrl, shouldCacheUrl } from '@/utils/constants/cacheConfig';

// 请求选项接口
interface RequestOptions {
  // 缓存相关
  useCache?: boolean;
  cacheTTL?: number;
  forceRefresh?: boolean;

  // 去重相关
  enableDeduplication?: boolean;
  requestKey?: string;

  // 取消相关
  signal?: AbortSignal;
  timeout?: number;

  // 其他选项
  retryCount?: number;
  retryDelay?: number;
}

// 创建全局请求去重器
const requestDeduplicator = createRequestDeduplicator({
  timeout: 30000,
  enableLogging: process.env.NODE_ENV === 'development',
});

/**
 * 增强的GET请求
 */
export const enhancedGet = async <T = any>(
  url: string,
  params?: any,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> => {
  const {
    useCache = shouldCacheUrl(url, 'GET'),
    cacheTTL = getCacheTTLForUrl(url),
    forceRefresh = false,
    enableDeduplication = true,
    requestKey,
    signal,
    timeout = 30000,
    retryCount = 0,
    retryDelay = 1000,
  } = options;

  // 生成请求键
  const key = requestKey || `GET:${url}:${JSON.stringify(params || {})}`;

  // 如果不强制刷新且启用缓存，先尝试从缓存获取
  if (!forceRefresh && useCache) {
    const cached = apiCache.get<ApiResponse<T>>(url, params);
    if (cached) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[增强请求] 缓存命中: ${key}`);
      }
      return cached;
    }
  }

  // 定义请求函数
  const requestFn = async (abortSignal?: AbortSignal): Promise<ApiResponse<T>> => {
    // 合并取消信号
    const combinedSignal = signal || abortSignal;

    let attempt = 0;
    while (attempt <= retryCount) {
      try {
        // 创建带超时的请求
        const timeoutPromise = new Promise<never>((_, reject) => {
          const timeoutId = setTimeout(() => {
            reject(new Error(`Request timeout after ${timeout}ms`));
          }, timeout);

          // 如果有取消信号，监听取消事件
          if (combinedSignal) {
            combinedSignal.addEventListener('abort', () => {
              clearTimeout(timeoutId);
              reject(new Error('Request aborted'));
            });
          }
        });

        // 执行实际请求
        const requestPromise = get<T>(url, params);

        // 竞争：请求完成 vs 超时/取消
        const response = await Promise.race([requestPromise, timeoutPromise]);

        // 缓存成功的响应
        if (response.code === 0 && useCache) {
          apiCache.set(url, response, params, cacheTTL);

          if (process.env.NODE_ENV === 'development') {
            console.log(`[增强请求] 缓存设置: ${key}`);
          }
        }

        return response;
      } catch (error) {
        attempt++;

        // 如果是取消或超时错误，不重试
        if (
          error instanceof Error &&
          (error.message.includes('aborted') || error.message.includes('timeout'))
        ) {
          throw error;
        }

        // 如果还有重试次数，等待后重试
        if (attempt <= retryCount) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`[增强请求] 重试 ${attempt}/${retryCount}: ${key}`);
          }
          await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
        } else {
          throw error;
        }
      }
    }

    throw new Error('Max retry attempts reached');
  };

  // 如果启用去重，使用去重器执行请求
  if (enableDeduplication) {
    return requestDeduplicator.execute(requestFn, url, params);
  } else {
    return requestFn(signal);
  }
};

/**
 * 增强的POST请求
 */
export const enhancedPost = async <T = any>(
  url: string,
  data?: any,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> => {
  const {
    enableDeduplication = true,
    requestKey,
    signal,
    timeout,
    retryCount = 0,
    retryDelay = 1000,
  } = options;
  const finalTimeout = timeout ?? 30000;

  // 生成请求键
  const key = requestKey || `POST:${url}:${JSON.stringify(data || {})}`;

  // 定义请求函数
  const requestFn = async (abortSignal?: AbortSignal): Promise<ApiResponse<T>> => {
    const combinedSignal = signal || abortSignal;

    let attempt = 0;
    while (attempt <= retryCount) {
      try {
        const timeoutPromise = new Promise<never>((_, reject) => {
          const timeoutId = setTimeout(() => {
            reject(new Error(`Request timeout after ${finalTimeout}ms`));
          }, finalTimeout);

          if (combinedSignal) {
            combinedSignal.addEventListener('abort', () => {
              clearTimeout(timeoutId);
              reject(new Error('Request aborted'));
            });
          }
        });

        const requestPromise = post<T>(url, data, { timeout: finalTimeout });
        const response = await Promise.race([requestPromise, timeoutPromise]);

        // POST请求成功后，清理相关缓存
        if (response.code === 0) {
          invalidateRelatedCache(url);
        }

        return response;
      } catch (error) {
        attempt++;

        if (
          error instanceof Error &&
          (error.message.includes('aborted') || error.message.includes('timeout'))
        ) {
          throw error;
        }

        if (attempt <= retryCount) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`[增强请求] 重试 ${attempt}/${retryCount}: ${key}`);
          }
          await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
        } else {
          throw error;
        }
      }
    }

    throw new Error('Max retry attempts reached');
  };

  if (enableDeduplication) {
    return requestDeduplicator.execute(requestFn, url, data);
  } else {
    return requestFn(signal);
  }
};

/**
 * 增强的PUT请求
 */
export const enhancedPut = async <T = any>(
  url: string,
  data?: any,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> => {
  const { enableDeduplication = true, requestKey, signal, timeout = 30000 } = options;

  // 生成请求键用于日志
  const key = requestKey || `PUT:${url}:${JSON.stringify(data || {})}`;

  if (process.env.NODE_ENV === 'development') {
    console.log(`[增强请求] PUT 请求: ${key}`);
  }

  const requestFn = async (abortSignal?: AbortSignal): Promise<ApiResponse<T>> => {
    const combinedSignal = signal || abortSignal;

    const timeoutPromise = new Promise<never>((_, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Request timeout after ${timeout}ms`));
      }, timeout);

      if (combinedSignal) {
        combinedSignal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          reject(new Error('Request aborted'));
        });
      }
    });

    const requestPromise = put<T>(url, data);
    const response = await Promise.race([requestPromise, timeoutPromise]);

    if (response.code === 0) {
      invalidateRelatedCache(url);
    }

    return response;
  };

  if (enableDeduplication) {
    return requestDeduplicator.execute(requestFn, url, data);
  } else {
    return requestFn(signal);
  }
};

/**
 * 增强的DELETE请求
 */
export const enhancedDelete = async <T = any>(
  url: string,
  params?: any,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> => {
  const { enableDeduplication = true, requestKey, signal, timeout = 30000 } = options;

  // 生成请求键用于日志
  const key = requestKey || `DELETE:${url}:${JSON.stringify(params || {})}`;

  if (process.env.NODE_ENV === 'development') {
    console.log(`[增强请求] DELETE 请求: ${key}`);
  }

  const requestFn = async (abortSignal?: AbortSignal): Promise<ApiResponse<T>> => {
    const combinedSignal = signal || abortSignal;

    const timeoutPromise = new Promise<never>((_, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Request timeout after ${timeout}ms`));
      }, timeout);

      if (combinedSignal) {
        combinedSignal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          reject(new Error('Request aborted'));
        });
      }
    });

    const requestPromise = del<T>(url, params);
    const response = await Promise.race([requestPromise, timeoutPromise]);

    if (response.code === 0) {
      invalidateRelatedCache(url);
    }

    return response;
  };

  if (enableDeduplication) {
    return requestDeduplicator.execute(requestFn, url, params);
  } else {
    return requestFn(signal);
  }
};

/**
 * 清理相关缓存
 */
const invalidateRelatedCache = (url: string): void => {
  const basePath = url.split('?')[0];
  const pathSegments = basePath.split('/').filter(Boolean);

  // 根据不同的API路径定义清理策略（用 deleteByPrefix 匹配带参数的缓存键）
  if (pathSegments.includes('asset')) {
    apiCache.deleteByPrefix('/asset/list');
    apiCache.deleteByPrefix('/canvas/asset_list');
  }

  if (pathSegments.includes('generation')) {
    apiCache.deleteByPrefix('/generation/');
    apiCache.deleteByPrefix('/asset/list');
  }

  if (pathSegments.includes('group')) {
    apiCache.deleteByPrefix('/group/');
    apiCache.deleteByPrefix('/canvas/asset_group');
    apiCache.deleteByPrefix('/asset/list');
  }

  if (process.env.NODE_ENV === 'development') {
    console.log(`[增强请求] 清理与 ${url} 相关的缓存`);
  }
};

/**
 * 取消请求
 */
export const cancelRequest = (url: string, params?: any) => {
  requestDeduplicator.cancel(url, params);
};

/**
 * 清理所有待处理请求
 */
export const clearAllRequests = () => {
  requestDeduplicator.clear();
};

// 导出增强请求对象
export const enhancedRequest = {
  get: enhancedGet,
  post: enhancedPost,
  put: enhancedPut,
  delete: enhancedDelete,
  cancel: cancelRequest,
  clear: clearAllRequests,
};

export default enhancedRequest;
